import { useRef, useEffect } from 'react';
import timiPicture from '../assets/timi.jpeg';
import { useTranslations } from '../context/TranslationContext';
import useAnalytics from '../hooks/useAnalytics';
import { scrollToId } from '../utils/smoothScroll';

export const Hero: React.FC = () => {
  const { t } = useTranslations();
  const { trackEvent } = useAnalytics();
  const sectionRef = useRef<HTMLElement>(null);
  const viewStartTime = useRef<number | null>(null);

  const scrollToContact = () => {
    trackEvent('hero_scroll_to_contact', {
      current_scroll_position: window.scrollY,
      timestamp: new Date().toISOString()
    });
    scrollToId('contact');
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewStartTime.current) {
            viewStartTime.current = Date.now();
            trackEvent('hero_view_start', { timestamp: new Date().toISOString() });
          } else if (!entry.isIntersecting && viewStartTime.current) {
            const viewDuration = Date.now() - viewStartTime.current;
            trackEvent('hero_view_complete', {
              view_duration_ms: viewDuration,
              view_duration_readable: `${(viewDuration / 1000).toFixed(1)}s`,
              timestamp: new Date().toISOString()
            });
            viewStartTime.current = null;
          }
        });
      },
      { threshold: 0.5 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [trackEvent]);

  // Split the title into sentences on ". " / trailing "." — italicise the final
  // sentence in accent color, keep the rest in normal weight. No period is
  // appended: each sentence already carries its own from the source string.
  const titleSentences = t.hero.title
    .split('.')
    .map((s) => s.trim())
    .filter(Boolean);
  const lastSentence = titleSentences.pop();
  const titleStart = titleSentences.join('. ') + (titleSentences.length ? '.' : '');

  return (
    <section id="home" className="min-h-screen flex items-center pt-32 pb-24 bg-cream" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-x-20 lg:gap-y-12 items-center lg:items-stretch w-full">
        <div className="order-1 lg:order-none lg:col-span-7 lg:col-start-1 lg:row-start-1 lg:pr-12" data-reveal>
          <div className="flex items-center gap-4 mb-10">
            <span className="number-tag">— 2013</span>
            <span className="eyebrow">{t.hero.eyebrow}</span>
          </div>
          <h1 className="display text-[52px] md:text-[72px] lg:text-[88px] mb-8">
            {titleStart}<br />
            <em className="italic text-accent">{lastSentence}</em>
          </h1>
          <p className="text-ink text-[15px] leading-[1.8] max-w-xl mb-12 lg:mb-0 font-light">
            {t.hero.description}
          </p>
        </div>

        {/* CTA sits in its own row so the portrait aligns with the headline and
            paragraph only, rather than running past the button. */}
        <div className="order-2 lg:order-none lg:col-span-7 lg:col-start-1 lg:row-start-2 lg:pr-12" data-reveal>
          <button className="btn-primary" onClick={scrollToContact} aria-label={t.hero.cta}>
            <span>{t.hero.cta}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          <div className="mt-6">
            <a
              href={t.contact.info.reviews.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] text-mute font-light link-grow"
              onClick={() => trackEvent('reviews_click', { location: 'hero' })}
            >
              <span className="text-accent">★</span>
              <span>{t.contact.info.reviews.value.replace('★', '').replace(/\s{2,}/g, ' ').trim()}</span>
            </a>
          </div>
        </div>
        <div className="order-3 lg:order-none lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2 lg:relative" data-reveal data-reveal-delay="200">
          {/* From lg the portrait spans both rows — headline/paragraph and the
              CTA — and is centred in that space, so it carries equal air above
              and below and reaches past the button. Absolute inside a stretched
              grid item keeps it out of the row height calculation; otherwise
              the image's own ratio would drive the layout. */}
          <div className="w-full lg:absolute lg:inset-0 lg:flex lg:items-center lg:justify-center">
            <img
              src={timiPicture}
              alt={t.hero.imageAlt}
              className="w-full h-auto lg:max-h-full lg:w-full object-contain"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
